import { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * The open/close behaviour every header menu needs, in one place.
 *
 * Two menus grew the same thirty lines independently — outside click, Escape,
 * close on navigate — which is two chances for a keyboard trap to appear in
 * only one of them.
 *
 * Opens on click rather than hover, deliberately: a hover menu is unreachable
 * by keyboard without extra handling, and on the touch devices most of this
 * audience uses the first tap becomes a hover that swallows the second.
 */
export function useDismissableMenu() {
  const [open, setOpen] = useState(false);
  const id = useId();
  const wrapper = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // A menu left open across a navigation would cover the page just arrived at.
  useEffect(() => setOpen(false), [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      // Focus goes back to the trigger, or the next Tab starts from the top of
      // the document.
      wrapper.current?.querySelector('button')?.focus();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return { open, setOpen, wrapper, id };
}
